import { CredentialStatus, STAFF_OR_ABOVE, UserStatus } from "@excel-center/shared";
import type { Database } from "@excel-center/supabase";
import { supabase } from "@/src/common/services/supabase.service";
import { RECENT_STUDENTS_LIMIT } from "@/src/dashboard/constants/dashboard.constants";

type SchoolFilter = string | null;

export interface DashboardKpis {
	totalStudents: number;
	activeEnrollments: number;
	jobPostings: number;
	staffMembers: number;
}

export type CredentialStatusSliceId = CredentialStatus | "other";

export interface CredentialStatusSlice {
	id: CredentialStatusSliceId;
	count: number;
}

export interface RecentStudentRow {
	id: string;
	firstName: string | null;
	lastName: string | null;
	email: string | null;
	status: string;
	createdAt: string;
}

export interface SchoolGlanceRow {
	id: string;
	name: string;
	visibility: Database["public"]["Enums"]["school_visibility"];
	studentCount: number;
}

async function countStudents(schoolId: SchoolFilter, activeOnly = false): Promise<number> {
	let query = supabase.from("students").select("*", { count: "exact", head: true });
	if (schoolId) query = query.eq("school_id", schoolId);
	if (activeOnly) query = query.eq("status", UserStatus.ACTIVE);
	const { count, error } = await query;
	if (error) throw error;
	return count ?? 0;
}

async function countJobs(schoolId: SchoolFilter): Promise<number> {
	let query = supabase.from("jobs").select("*", { count: "exact", head: true });
	if (schoolId) query = query.eq("school_id", schoolId);
	const { count, error } = await query;
	if (error) throw error;
	return count ?? 0;
}

async function countStaff(schoolId: SchoolFilter, scopeSchoolIds: readonly string[] | null): Promise<number> {
	const schoolScope = schoolId ? [schoolId] : scopeSchoolIds ? [...scopeSchoolIds] : null;
	if (schoolScope) {
		const { data, error } = await supabase.from("staff_schools").select("staff_id").in("school_id", schoolScope);
		if (error) throw error;
		return new Set((data ?? []).map((r) => r.staff_id)).size;
	}
	const { count, error } = await supabase
		.from("profiles")
		.select("*", { count: "exact", head: true })
		.in("role", [...STAFF_OR_ABOVE]);
	if (error) throw error;
	return count ?? 0;
}

export async function getKpis(schoolId: SchoolFilter, scopeSchoolIds: readonly string[] | null = null): Promise<DashboardKpis> {
	const [totalStudents, activeEnrollments, jobPostings, staffMembers] = await Promise.all([
		countStudents(schoolId, false),
		countStudents(schoolId, true),
		countJobs(schoolId),
		countStaff(schoolId, scopeSchoolIds),
	]);
	return { totalStudents, activeEnrollments, jobPostings, staffMembers };
}

export async function getCredentialStatus(schoolId: SchoolFilter): Promise<CredentialStatusSlice[]> {
	let credQuery = supabase.from("student_credentials").select("status, students!inner(school_id)");
	if (schoolId) credQuery = credQuery.eq("students.school_id", schoolId);
	const { data, error } = await credQuery;
	if (error) throw error;

	const counts: Record<CredentialStatusSliceId, number> = {
		[CredentialStatus.IN_PROGRESS]: 0,
		[CredentialStatus.PASS]: 0,
		[CredentialStatus.FAIL]: 0,
		[CredentialStatus.DROP]: 0,
		other: 0,
	};
	for (const row of data ?? []) {
		const s = (row.status ?? "").trim().toLowerCase();
		if (s.includes("progress")) counts[CredentialStatus.IN_PROGRESS]++;
		else if (s.includes("pass")) counts[CredentialStatus.PASS]++;
		else if (s.includes("fail")) counts[CredentialStatus.FAIL]++;
		else if (s.includes("drop")) counts[CredentialStatus.DROP]++;
		else counts.other++;
	}
	const slices: CredentialStatusSlice[] = [
		{ id: CredentialStatus.IN_PROGRESS, count: counts[CredentialStatus.IN_PROGRESS] },
		{ id: CredentialStatus.PASS, count: counts[CredentialStatus.PASS] },
		{ id: CredentialStatus.FAIL, count: counts[CredentialStatus.FAIL] },
		{ id: CredentialStatus.DROP, count: counts[CredentialStatus.DROP] },
	];
	if (counts.other > 0) slices.push({ id: "other", count: counts.other });
	return slices;
}

export async function getRecentStudents(schoolId: SchoolFilter, limit = RECENT_STUDENTS_LIMIT): Promise<RecentStudentRow[]> {
	let query = supabase
		.from("students")
		.select("id, first_name, last_name, email, status, created_at")
		.order("created_at", { ascending: false })
		.limit(limit);
	if (schoolId) query = query.eq("school_id", schoolId);
	const { data, error } = await query;
	if (error) throw error;
	return (data ?? []).map((row) => ({
		id: row.id,
		firstName: row.first_name,
		lastName: row.last_name,
		email: row.email,
		status: row.status,
		createdAt: row.created_at,
	}));
}

export async function getSchoolsAtGlance(): Promise<SchoolGlanceRow[]> {
	const { data: schools, error } = await supabase.from("schools").select("id, name, visibility").order("name", { ascending: true });
	if (error) throw error;

	const rows = await Promise.all(
		(schools ?? []).map(async (school) => {
			const { count } = await supabase.from("students").select("*", { count: "exact", head: true }).eq("school_id", school.id);
			return {
				id: school.id,
				name: school.name,
				visibility: school.visibility,
				studentCount: count ?? 0,
			};
		}),
	);
	return rows;
}

export async function listAccessibleSchools(): Promise<{ id: string; name: string }[]> {
	const { data, error } = await supabase.from("schools").select("id, name").order("name", { ascending: true });
	if (error) throw error;
	return data ?? [];
}
